import logger from '@/layers/support/monitoring/logger';
import { useCallback, useEffect, useRef } from 'react';

type LoggerConfig = {
  module: string;
  props?: Record<string, any>;
  state?: Record<string, any>;
  data?: {
    values: Record<string, any>;
    loading?: Record<string, boolean>;
    error?: any;
  };
};

type LogFunction = (event: string, data?: Record<string, any>) => void;

export function useLogger({ module, props, state, data }: LoggerConfig): LogFunction {
  // Use refs to track previous values and prevent duplicate logging
  const hasLoggedProps = useRef(false);
  const prevState = useRef(state);
  const prevData = useRef(data);

  // Log props only once on mount
  useEffect(() => {
    if (props && !hasLoggedProps.current) {
      logger.debug(`${module} :: Props`, props);
      hasLoggedProps.current = true;
    }
  }); // Empty deps = runs once on mount

  // Log state changes
  useEffect(() => {
    if (state && JSON.stringify(state) !== JSON.stringify(prevState.current)) {
      logger.debug(`${module} :: State`, {
        current: state,
        previous: prevState.current,
        changes: Object.keys(state).filter(
          key => JSON.stringify(state[key]) !== JSON.stringify(prevState.current?.[key]),
        ),
      });
      prevState.current = state;
    }
  }, [state, module]);

  // Log data changes
  useEffect(() => {
    if (!data) return;

    const prevDataValue = prevData.current;
    const hasDataChanged = JSON.stringify(data) !== JSON.stringify(prevDataValue);

    if (hasDataChanged) {
      const isLoading = Object.values(data.loading || {}).some(loading => loading);
      const changedValues = Object.keys(data.values || {}).filter(
        key => JSON.stringify(data.values[key]) !== JSON.stringify(prevDataValue?.values?.[key]),
      );
      const changedLoadingStates = Object.keys(data.loading || {}).filter(
        key => data.loading?.[key] !== prevDataValue?.loading?.[key],
      );

      // Log loading state changes
      if (changedLoadingStates.length > 0) {
        logger.debug(`${module} :: Loading State Changed`, {
          loading: data.loading,
          changedStates: changedLoadingStates,
        });
      }

      // Log data value changes
      if (changedValues.length > 0 && !isLoading) {
        logger.debug(`${module} :: Data Updated`, {
          values: data.values,
          changedFields: changedValues,
          error: data.error,
        });
      }

      // Log errors if present
      if (data.error !== prevDataValue?.error) {
        logger.debug(`${module} :: Error State Changed`, {
          error: data.error,
          previousError: prevDataValue?.error,
        });
      }

      prevData.current = data;
    }
  }, [data, module]);

  // Event logging function
  const log = useCallback(
    (event: string, data?: Record<string, any>) => {
      logger.debug(`${module} :: ${event}`, data || {});
    },
    [module],
  );

  return log;
}
